import React, { useState, useCallback, useEffect } from 'react';
import { supabase } from '../../../supabaseClient';
import { useAdmin } from '../../../context/AdminContext';

const STAT_LABELS = {
    points:          'PTS',
    three_pm:        '3PM',
    two_pm:          '2PM',
    ft_made:         'FTM',
    rebounds:        'REB',
    assists:         'AST',
    personal_fouls:  'PF',
    technical_fouls: 'TF',
};

const parseLocalDate = (str) => {
    const [date] = (str || '').split('T');
    const [y, m, d] = date.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
    });
};

const getDiff = (original, requested) => {
    const diffs = {};
    Object.keys(STAT_LABELS).forEach(key => {
        const orig = Number(original?.[key] ?? 0);
        const req  = Number(requested?.[key]  ?? 0);
        if (orig !== req) diffs[key] = { from: orig, to: req };
    });
    return diffs;
};

const StatusBadge = ({ status }) => {
    const styles = {
        pending:  'bg-amber-100  dark:bg-amber-900/40  text-amber-700  dark:text-amber-300',
        approved: 'bg-green-100  dark:bg-green-900/40  text-green-700  dark:text-green-300',
        rejected: 'bg-red-100    dark:bg-red-900/40    text-red-700    dark:text-red-300',
    };
    return (
        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${styles[status] ?? ''}`}>
            {status}
        </span>
    );
};

const StatAdjustmentPanel = () => {
    const { adminId } = useAdmin();

    const [requests,    setRequests]    = useState([]);
    const [loading,     setLoading]     = useState(false);
    const [filter,      setFilter]      = useState('pending');
    const [processing,  setProcessing]  = useState({});
    const [notesInput,  setNotesInput]  = useState({});
    const [expandedReq, setExpandedReq] = useState(null);
    // NEW: map of player_id → { name, jersey_number }
    const [playerMap,   setPlayerMap]   = useState({});

    const loadRequests = useCallback(async () => {
        setLoading(true);
        const query = supabase
            .from('stat_adjustment_requests')
            .select(`
                *,
                game:games(id, game_date, team1:teams!games_team1_id_fkey(name), team2:teams!games_team2_id_fkey(name)),
                team:teams(id, name),
                captain:profiles(id, name)
            `)
            .order('created_at', { ascending: false });

        if (filter !== 'all') query.eq('status', filter);

        const { data, error } = await query;
        if (!error) setRequests(data || []);
        setLoading(false);
    }, [filter]);

    useEffect(() => { loadRequests(); }, [loadRequests]);

    // NEW: whenever requests change, collect all unique player IDs and fetch their info
    useEffect(() => {
        if (!requests.length) return;

        const allPlayerIds = new Set();
        requests.forEach(req => {
            Object.keys(req.requested_stats || {}).forEach(id => allPlayerIds.add(id));
        });

        const ids = [...allPlayerIds];
        if (!ids.length) return;

        // Only fetch players we don't already have in the map
        const missing = ids.filter(id => !playerMap[id]);
        if (!missing.length) return;

        supabase
            .from('players')
            .select('id, name, jersey_number')
            .in('id', missing)
            .then(({ data }) => {
                if (!data) return;
                setPlayerMap(prev => {
                    const next = { ...prev };
                    data.forEach(p => { next[p.id] = p; });
                    return next;
                });
            });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requests]);

    const handleDecision = async (req, decision) => {
        setProcessing(prev => ({ ...prev, [req.id]: true }));
        try {
            const { error } = await supabase.functions.invoke('admin-actions', {
                body: {
                    action:  'resolveStatAdjustment',
                    adminId,
                    payload: {
                        requestId:      req.id,
                        decision,
                        adminNotes:     notesInput[req.id] || null,
                        gameId:         req.game_id,
                        requestedStats: req.requested_stats,
                    },
                },
            });
            if (error) throw error;
            await loadRequests();
            setExpandedReq(null);
        } catch (err) {
            alert(`Error: ${err.message}`);
        } finally {
            setProcessing(prev => ({ ...prev, [req.id]: false }));
        }
    };

    const pendingCount = requests.filter(r => r.status === 'pending').length;

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Stat Adjustment Requests</h2>
                    {pendingCount > 0 && (
                        <span className="bg-amber-500 text-white text-xs font-bold px-2.5 py-1 rounded-full">
                            {pendingCount} pending
                        </span>
                    )}
                </div>

                {/* Filter pills */}
                <div className="flex gap-2">
                    {['pending', 'approved', 'rejected', 'all'].map(f => (
                        <button
                            key={f}
                            onClick={() => setFilter(f)}
                            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors capitalize ${
                                filter === f
                                    ? 'bg-orange-600 text-white'
                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                            }`}
                        >
                            {f}
                        </button>
                    ))}
                </div>
            </div>

            {loading ? (
                <p className="text-gray-500 dark:text-gray-400 text-center py-12">Loading requests…</p>
            ) : requests.length === 0 ? (
                <div className="text-center py-16 text-gray-400 dark:text-gray-500">
                    <div className="text-4xl mb-3">📋</div>
                    <p className="font-medium">No {filter !== 'all' ? filter : ''} requests</p>
                </div>
            ) : (
                <div className="space-y-4">
                    {requests.map(req => {
                        const isExpanded   = expandedReq === req.id;
                        const isProcessing = !!processing[req.id];

                        // FIX 2: removed `truncate` — game label now wraps on mobile
                        const gameLabel = req.game
                            ? `${req.game.team1?.name} vs ${req.game.team2?.name} · ${parseLocalDate(req.game.game_date)}`
                            : 'Unknown game';

                        const playerDiffs = Object.entries(req.requested_stats || {}).map(([playerId, reqStats]) => {
                            const origStats = req.original_stats?.[playerId] || {};
                            const diff      = getDiff(origStats, reqStats);
                            return { playerId, reqStats, origStats, diff, hasDiff: Object.keys(diff).length > 0 };
                        }).filter(p => p.hasDiff);

                        return (
                            <div key={req.id}
                                className="border dark:border-gray-700 rounded-2xl overflow-hidden bg-white dark:bg-gray-800 shadow-sm"
                            >
                                {/* Card header */}
                                <button
                                    onClick={() => setExpandedReq(isExpanded ? null : req.id)}
                                    // FIX 2: items-start so badge and chevron align to top when label wraps
                                    className="w-full text-left px-5 py-4 flex flex-wrap items-start gap-3 hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors"
                                >
                                    <StatusBadge status={req.status} />

                                    <div className="flex-1 min-w-0">
                                        {/* FIX 2: removed `truncate` — wraps naturally now */}
                                        <p className="font-semibold text-gray-900 dark:text-white leading-snug">
                                            {gameLabel}
                                        </p>
                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                            Submitted by <span className="font-medium">{req.captain?.name ?? 'Unknown'}</span>
                                            {' · '}{req.team?.name}
                                            {' · '}{new Date(req.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                        </p>
                                    </div>

                                    <span className="text-xs text-gray-400 shrink-0">
                                        {playerDiffs.length} player{playerDiffs.length !== 1 ? 's' : ''} changed
                                    </span>

                                    <span className={`text-gray-400 transition-transform duration-200 text-xs mt-0.5 ${isExpanded ? 'rotate-180' : ''}`}>▼</span>
                                </button>

                                {/* Expanded detail */}
                                {isExpanded && (
                                    <div className="border-t dark:border-gray-700 px-5 py-5 space-y-5">

                                        <div className="space-y-3">
                                            {playerDiffs.map(({ playerId, diff }) => {
                                                // FIX 1: resolve player name + jersey from map
                                                const player = playerMap[playerId];
                                                const playerLabel = player
                                                    ? `#${player.jersey_number} ${player.name}`
                                                    : `ID: ${playerId.slice(0, 8)}…`;

                                                return (
                                                    <div key={playerId}
                                                        className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4"
                                                    >
                                                        {/* FIX 1: show name + jersey instead of raw UUID */}
                                                        <p className="text-sm font-bold text-gray-800 dark:text-gray-100 mb-3">
                                                            {playerLabel}
                                                        </p>
                                                        <div className="flex flex-wrap gap-3">
                                                            {Object.entries(diff).map(([stat, { from, to }]) => (
                                                                <div key={stat}
                                                                    className="flex flex-col items-center bg-white dark:bg-gray-800 border dark:border-gray-600 rounded-lg px-3 py-2 min-w-[60px]"
                                                                >
                                                                    <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1">
                                                                        {STAT_LABELS[stat]}
                                                                    </span>
                                                                    <div className="flex items-center gap-1.5 text-sm font-mono">
                                                                        <span className="text-gray-400 line-through">{from}</span>
                                                                        <span className="text-gray-400">→</span>
                                                                        <span className={`font-bold ${to > from ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'}`}>
                                                                            {to}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>

                                        {req.status === 'pending' && (
                                            <div className="space-y-3">
                                                <textarea
                                                    placeholder="Admin notes (optional — captain will see this)"
                                                    value={notesInput[req.id] ?? ''}
                                                    onChange={e => setNotesInput(prev => ({ ...prev, [req.id]: e.target.value }))}
                                                    rows={2}
                                                    className="w-full border dark:border-gray-600 rounded-xl px-4 py-2.5 text-sm
                                                               bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                                                               placeholder-gray-400 focus:outline-none focus:border-orange-500 resize-none"
                                                />
                                                <div className="flex gap-3">
                                                    <button
                                                        onClick={() => handleDecision(req, 'rejected')}
                                                        disabled={isProcessing}
                                                        className="flex-1 py-2.5 text-sm font-semibold rounded-xl border-2 border-red-300 dark:border-red-800
                                                                   text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20
                                                                   disabled:opacity-50 transition"
                                                    >
                                                        ✗ Reject
                                                    </button>
                                                    <button
                                                        onClick={() => handleDecision(req, 'approved')}
                                                        disabled={isProcessing}
                                                        className="flex-1 py-2.5 text-sm font-semibold rounded-xl
                                                                   bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-400
                                                                   text-white transition"
                                                    >
                                                        {isProcessing ? 'Saving…' : '✓ Approve & Apply'}
                                                    </button>
                                                </div>
                                            </div>
                                        )}

                                        {req.status !== 'pending' && req.admin_notes && (
                                            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                                                <span className="font-semibold text-gray-700 dark:text-gray-200">Admin note: </span>
                                                {req.admin_notes}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default StatAdjustmentPanel;